import { useRef, useState } from 'react';
import { MessageErreur, Pastille } from '../ui/Primitives';
import { useToast } from '../ui/useToast';
import { dateReunion } from '../../lib/useFormateurs';
import { urlModelePedagogique, type DepotsModele } from '../../lib/usePedagogique';
import { messageErreur } from '../parametrage/commun';

function taille(octets: number): string {
  return octets < 1024 * 1024 ? `${Math.max(1, Math.round(octets / 1024))} Ko` : `${(octets / 1024 / 1024).toFixed(1)} Mo`;
}

/**
 * Une ligne de modèle déposé (TF-477), partagée par « Documents pédagogiques » et
 * l'onglet « Modèles de documents » de « Paramétrage » : version en vigueur,
 * dépôt d'une nouvelle version, versions précédentes lisibles. Le dépôt le
 * plus récent sert aux demandes à venir ; ce qui a déjà été remis garde sa
 * copie. Sans `deposer`, la ligne se lit seulement.
 */
export function LigneModeleDepose({
  libelle,
  precision,
  depots,
  sansModele,
  etatAbsent = { texte: 'À déposer', ton: 'warning' },
  libelleDepot = 'Déposer le modèle',
  deposer,
}: {
  libelle: string;
  /** Mention sous le libellé : code de la formation, formation désactivée… */
  precision?: string;
  depots: DepotsModele;
  /** Ce que vaut l'absence de modèle. */
  sansModele: string;
  etatAbsent?: { texte: string; ton: 'warning' | 'neutral' | 'info' };
  libelleDepot?: string;
  deposer?: (fichier: File) => Promise<string>;
}): JSX.Element {
  const toast = useToast();
  const champ = useRef<HTMLInputElement>(null);
  const [enCours, setEnCours] = useState(false);
  const [versionsOuvertes, setVersionsOuvertes] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const { enVigueur } = depots;
  const anciennes = depots.versions.slice(1);
  const action = enVigueur ? 'Déposer une nouvelle version' : libelleDepot;

  const envoyer = (fichier: File | undefined): void => {
    if (!fichier || !deposer) return;
    setErreur(null);
    setEnCours(true);
    deposer(fichier)
      .then((message) => toast(message))
      .catch((refus: unknown) => setErreur(messageErreur(refus, 'Le modèle n’a pas pu être déposé.')))
      .finally(() => {
        setEnCours(false);
        if (champ.current) champ.current.value = '';
      });
  };

  return (
    <div className="space-y-2 px-4 py-3 sm:px-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-codialis-navy">{libelle}</span>
            {enVigueur ? (
              <Pastille texte="Déposé" ton="success" />
            ) : (
              <Pastille texte={etatAbsent.texte} ton={etatAbsent.ton} />
            )}
          </div>
          {precision && <div className="text-[11px] text-codialis-gray">{precision}</div>}
          <div className="mt-0.5 text-[11px] text-codialis-gray">
            {enVigueur ? (
              <>
                <a
                  href={urlModelePedagogique(enVigueur.id)}
                  target="_blank"
                  rel="noreferrer"
                  className="break-all font-semibold text-codialis-sky hover:underline"
                >
                  {enVigueur.nomFichier}
                </a>{' '}
                · {taille(enVigueur.tailleOctets)} · déposé le {dateReunion(enVigueur.deposeLe)}
                {enVigueur.deposePar ? ` par ${enVigueur.deposePar}` : ''}
              </>
            ) : (
              sansModele
            )}
          </div>
        </div>
        {deposer && (
          <label
            className={`inline-flex min-h-[44px] shrink-0 cursor-pointer items-center justify-center rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50 focus-within:ring-2 focus-within:ring-codialis-sky ${
              enCours ? 'pointer-events-none opacity-60' : ''
            }`}
          >
            {enCours ? 'Dépôt…' : action}
            <input
              ref={champ}
              type="file"
              accept="application/pdf,.pdf"
              className="sr-only"
              aria-label={`${action} · ${libelle}`}
              disabled={enCours}
              onChange={(evenement) => envoyer(evenement.target.files?.[0])}
            />
          </label>
        )}
      </div>
      {erreur && <MessageErreur message={erreur} />}
      {anciennes.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setVersionsOuvertes(!versionsOuvertes)}
            aria-expanded={versionsOuvertes}
            className="inline-flex min-h-[40px] items-center text-xs font-semibold text-codialis-sky hover:underline"
          >
            {versionsOuvertes ? 'Masquer' : 'Voir'} {anciennes.length} version{anciennes.length > 1 ? 's' : ''}{' '}
            précédente{anciennes.length > 1 ? 's' : ''}
          </button>
          {versionsOuvertes && (
            <ul className="mt-1 space-y-1 text-[11px] text-codialis-gray">
              {anciennes.map((version) => (
                <li key={version.id}>
                  <a
                    href={urlModelePedagogique(version.id)}
                    target="_blank"
                    rel="noreferrer"
                    className="break-all font-semibold text-codialis-sky hover:underline"
                  >
                    {version.nomFichier}
                  </a>{' '}
                  · {dateReunion(version.deposeLe)}
                  {version.deposePar ? ` · ${version.deposePar}` : ''}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
